import React from "react";

import { ButtonStandard, type ButtonStandardProps, Text } from "@performyard/py-components";

type HeaderAction = Omit<ButtonStandardProps, "children" | "dangerouslySetClassName"> & {
	label: string;
};

type GeneratedContentContainerProps = {
	/** Title displayed in the header (e.g., "Personal Goals", "Suggested Actions") */
	title: string;
	/** Optional action button in the header (e.g., "Edit Goals") */
	headerAction?: HeaderAction;
	/** Content to render inside the container (goal cards, action items, etc.) */
	children: React.ReactNode;
	/** Optional className for additional styling */
	className?: string;
};

/**
 * A container component for AI-generated/suggested content in the Ask PerformYard chat.
 * Used as a parent shell for displaying grouped items like goals, action items,
 * agenda items, or form questions suggested by the LLM.
 *
 * @example
 * ```tsx
 * <GeneratedContentContainer
 *   title="Personal Goals"
 *   headerAction={{
 *     label: "Edit Goals",
 *     iconLeft: faPencil,
 *     onClick: () => openEditDrawer(),
 *   }}
 * >
 *   <GoalPreviewCard goal={goal1} />
 *   <GoalPreviewCard goal={goal2} />
 * </GeneratedContentContainer>
 * ```
 */
export const GeneratedContentContainer: React.FC<GeneratedContentContainerProps> = ({
	title,
	headerAction,
	children,
	className = "",
}) => {
	return (
		<div
			className={`flex min-w-0 max-w-full flex-col gap-2 rounded-2 bg-neutral-1 px-4 py-2 ${className}`}
			data-testid="generated-content-container"
		>
			{/* Header with title and optional action */}
			<div className="flex h-6 items-center justify-between">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9" dangerouslySetClassName="py-line-clamp-1">
					{title}
				</Text>

				{headerAction && (
					<ButtonStandard
						variant="text"
						color="neutral"
						size="medium"
						{...headerAction}
						dangerouslySetClassName="w-fit"
					>
						{headerAction.label}
					</ButtonStandard>
				)}
			</div>

			{/* Content area for children */}
			<div className="flex flex-col">{children}</div>
		</div>
	);
};

export default GeneratedContentContainer;
